import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = "https://appapi.unpaidfinancial.com/api";

const fmtMoney = (n, dev = "EUR") =>
  `${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${dev}`;

const initials = (u) =>
  ((`${u?.prenom ?? ""} ${u?.nom ?? ""}`.trim() || u?.name || "?")
    .split(" ")
    .map((s) => s.charAt(0))
    .join("")
    .substring(0, 2)
    .toUpperCase());

const VirtualCard = ({ card, holder }) => (
  <div className="vf-card">
    <div className="vf-chip" />
    <div className="vf-brand">{card?.brand || "VIRTUAL"}</div>
    <div className="vf-number">{card?.number_masked || "•••• •••• •••• ••••"}</div>
    <div className="d-flex justify-content-between mt-2">
      <div className="vf-label">
        Titulaire
        <div className="vf-value">{holder || "Titulaire"}</div>
      </div>
      <div className="vf-label text-end">
        Expire
        <div className="vf-value">
          {card ? `${card.exp_month}/${card.exp_year}` : "MM/AAAA"}
        </div>
      </div>
    </div>
  </div>
);

export default function AdminCrédit() {
  const token = useMemo(() => localStorage.getItem("accessToken"), []);
  const auth = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Comptes existants (bénéficiaires possibles)
  const [accounts, setAccounts] = useState([]);
  const [accPage, setAccPage] = useState(1);
  const [accLastPage, setAccLastPage] = useState(1);
  const [accSearch, setAccSearch] = useState("");
  const [loadingAcc, setLoadingAcc] = useState(true);

  // Historique admin
  const [hist, setHist] = useState([]);
  const [histPage, setHistPage] = useState(1);
  const [histLastPage, setHistLastPage] = useState(1);
  const [histSearch, setHistSearch] = useState("");
  const [loadingHist, setLoadingHist] = useState(true);

  // Modal “nouveau virement”
  const [newOpen, setNewOpen] = useState(false);
  const [selected, setSelected] = useState(null); // {id, user, solde, devise, card}
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Charger bénéficiaires
  const loadAccounts = async (page = accPage, search = accSearch) => {
    setLoadingAcc(true);
    try {
      const { data } = await axios.get(`${API}/bank/accounts`, {
        params: { page, per_page: 12, search: search || undefined },
        ...auth,
      });
      setAccounts(data.data || []);
      setAccPage(data.current_page || 1);
      setAccLastPage(data.last_page || 1);
    } finally {
      setLoadingAcc(false);
    }
  };

  // Charger historique
  const loadHistory = async (page = histPage, search = histSearch) => {
    setLoadingHist(true);
    try {
      const { data } = await axios.get(`${API}/bank/transactions/admin`, {
        params: { page, per_page: 10, search: search || undefined },
        ...auth,
      });
      setHist(data.data || []);
      setHistPage(data.current_page || 1);
      setHistLastPage(data.last_page || 1);
    } finally {
      setLoadingHist(false);
    }
  };

  useEffect(() => {
    loadAccounts(1, "");
  }, []);
  useEffect(() => {
    loadHistory(1, "");
  }, []);

  const openNew = () => {
    setSelected(null);
    setAmount("");
    setDescription("");
    setNewOpen(true);
  };
  const closeNew = () => {
    setNewOpen(false);
    setSelected(null);
    setAmount("");
    setDescription("");
    setConfirmOpen(false);
  };

  const holderName = (u) =>
    (`${u?.prenom ?? ""} ${u?.nom ?? ""}`.trim()) || u?.name || "Utilisateur";

  const startConfirm = (acc) => {
    setSelected(acc);
    if (!newOpen) setNewOpen(true);
  };

  const canSubmit = selected && Number(amount) > 0;

  const doCredit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/bank/accounts/${selected.user.id}/credit`,
        { amount: Number(amount), description: description || undefined },
        auth
      );
      await Promise.all([loadHistory(1, histSearch), loadAccounts(accPage, accSearch)]);
      setConfirmOpen(false);
      closeNew();
      alert("Virement admin effectué");
    } catch (e) {
      alert(e?.response?.data?.error || "Erreur lors du virement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-3">
      <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center justify-content-between mb-3">
        <h4 className="m-0">Virements Admin</h4>
        <button className="btn btn-primary" onClick={openNew}>
          <i className="fas fa-plus me-2"></i> Nouveau virement
        </button>
      </div>

      {/* Historique */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center mb-2">
            <h6 className="m-0">Historique des virements admin</h6>
            <div className="d-flex gap-2">
              <input
                className="form-control"
                placeholder="Rechercher (nom/email)"
                style={{ maxWidth: 280 }}
                value={histSearch}
                onChange={(e) => setHistSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadHistory(1, e.currentTarget.value)}
              />
              <button
                className="btn btn-outline-secondary"
                onClick={() => loadHistory(1, histSearch)}
              >
                Rechercher
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-striped align-middle">
              <thead>
                <tr>
                  <th>Bénéficiaire</th>
                  <th>Email</th>
                  <th>Montant</th>
                  <th>Devise</th>
                  <th>Date</th>
                  <th>Motif</th>
                </tr>
              </thead>
              <tbody>
                {loadingHist ? (
                  <tr>
                    <td colSpan={6}>Chargement…</td>
                  </tr>
                ) : hist.length === 0 ? (
                  <tr>
                    <td colSpan={6}>Aucun virement admin.</td>
                  </tr>
                ) : (
                  hist.map((t) => {
                    const u = t.to_account?.user || t.toAccount?.user;
                    return (
                      <tr key={t.id}>
                        <td>{u ? holderName(u) : "—"}</td>
                        <td>{u?.email || "—"}</td>
                        <td>
                          {Number(t.amount).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td>{t.to_account?.devise || t.toAccount?.devise || "EUR"}</td>
                        <td>{new Date(t.created_at).toLocaleString()}</td>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: 280 }}
                          title={t.description || ""}
                        >
                          {t.description || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* MODAL */}
      {newOpen && (
        <div
          className="modal-backdrop-custom"
          onClick={closeNew}
          onKeyDown={(e) => e.key === "Escape" && closeNew()}
          tabIndex={-1}
        >
          <div
            className="modal-card modal-lg modal-card-scroll"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h6 className="mb-3">Nouveau virement admin</h6>

            {/* Sélecteur bénéficiaire */}
            <div className="d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center mb-2">
              <strong className="m-0">Choisir un bénéficiaire</strong>
              <div className="d-flex gap-2">
                <input
                  className="form-control"
                  placeholder="Filtrer (nom/email)"
                  style={{ maxWidth: 260 }}
                  value={accSearch}
                  onChange={(e) => setAccSearch(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && loadAccounts(1, e.currentTarget.value)
                  }
                />
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => loadAccounts(1, accSearch)}
                >
                  Filtrer
                </button>
              </div>
            </div>

            <div className="row g-3">
              {loadingAcc ? (
                <div className="col-12">Chargement des comptes…</div>
              ) : accounts.length === 0 ? (
                <div className="col-12">Aucun compte trouvé.</div>
              ) : (
                accounts.map((acc) => {
                  const name = holderName(acc.user);
                  return (
                    <div className="col-12 col-md-6" key={acc.id}>
                      <div
                        className={`card selectable ${
                          selected?.id === acc.id ? "selected" : ""
                        }`}
                        onClick={() => startConfirm(acc)}
                      >
                        <div className="card-body">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            <div className="avatar-circle" title={name}>
                              {initials(acc.user)}
                            </div>
                            <div>
                              <div className="fw-bold">{name}</div>
                              <div className="text-muted" style={{ fontSize: 13 }}>
                                {acc.user?.email}
                              </div>
                            </div>
                          </div>
                          <VirtualCard card={acc.card} holder={name} />
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <span className="text-muted">Solde</span>
                            <div className="fw-semibold">
                              {fmtMoney(acc.solde, acc.devise)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Saisie */}
            <div className="mt-4">
              <h6 className="mb-2">Saisie</h6>
              {!selected ? (
                <div className="alert alert-info mb-0">
                  Sélectionnez un bénéficiaire ci-dessus.
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (Number(amount) > 0) setConfirmOpen(true);
                  }}
                >
                  <div className="row g-3">
                    <div className="col-12 col-sm-6">
                      <label className="form-label">Montant</label>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        className="form-control"
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12 col-sm-6">
                      <label className="form-label">Motif (optionnel)</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength={255}
                        placeholder="Crédit initial, bonus…"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSelected(null);
                        setAmount("");
                        setDescription("");
                      }}
                    >
                      Réinitialiser
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!selected || Number(amount) <= 0}
                    >
                      Continuer
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Confirmation */}
            {confirmOpen && selected && (
              <div className="mt-3">
                <div className="receipt">
                  <div className="receipt-hd">Confirmation de virement admin</div>
                  <div className="receipt-row">
                    <span>Bénéficiaire</span>
                    <span>{holderName(selected.user)}</span>
                  </div>
                  <div className="receipt-row">
                    <span>Email</span>
                    <span>{selected.user?.email}</span>
                  </div>
                  <div className="receipt-row">
                    <span>Montant</span>
                    <span>{fmtMoney(amount, selected.devise)}</span>
                  </div>
                  {description && (
                    <div className="receipt-row">
                      <span>Motif</span>
                      <span>{description}</span>
                    </div>
                  )}
                  <div className="receipt-foot">
                    Vérifiez les informations avant de confirmer.
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2 mt-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setConfirmOpen(false)}
                  >
                    Annuler
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={doCredit}
                    disabled={submitting}
                  >
                    {submitting ? "Virement…" : "Confirmer"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        .avatar-circle { width:56px;height:56px;border-radius:50%;background:#2563eb;color:#fff;display:flex;align-items:center;justify-content:center;font-weight:700; }
        .vf-card { background: linear-gradient(135deg, #1f2937, #111827); color: #e5e7eb; border-radius: 16px; padding: 14px 16px; position: relative; box-shadow: 0 8px 20px rgba(0,0,0,.25); min-height: 110px; }
        .vf-chip { width: 34px; height: 24px; background: linear-gradient(145deg,#d1b65e,#a8892d); border-radius: 6px; opacity:.9; }
        .vf-brand { position:absolute; right:16px; top:12px; font-weight:700; letter-spacing:.6px; opacity:.9; }
        .vf-number { letter-spacing:2px; font-size:18px; margin-top:10px; }
        .vf-label { font-size:11px; opacity:.8; }
        .vf-value { font-size:13px; font-weight:600; }
        
        .modal-backdrop-custom {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.45);
          z-index: 1050;
          padding: 1rem;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: auto;
        }
        .modal-card {
          background:#fff;
          border-radius:12px;
          padding:16px;
          width:100%;
          max-width:520px;
          box-shadow:0 12px 36px rgba(0,0,0,.2);
        }
        .modal-card.modal-lg { max-width:980px; }
        .modal-card-scroll {
          max-height: calc(100vh - 2rem);
          overflow: auto;
        }
        @media (max-width: 768px) {
          .modal-backdrop-custom { align-items: flex-start; }
          .modal-card.modal-lg { max-width: 100%; }
          .modal-card-scroll { margin-top:1rem;margin-bottom:1rem;max-height:calc(100vh - 2rem); }
        }

        .card.selectable { cursor:pointer; transition:transform .12s ease, box-shadow .12s ease; }
        .card.selectable:hover { transform: translateY(-1px); box-shadow:0 8px 20px rgba(0,0,0,.12); }
        .card.selectable.selected { outline:2px solid #2563eb; }
        .receipt { border:1px dashed #d1d5db; padding:12px; border-radius:8px; background:#fafafa; }
        .receipt-hd { font-weight:700; margin-bottom:6px; }
        .receipt-row { display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px dashed #e5e7eb; }
        .receipt-row:last-child { border-bottom:0; }
        .receipt-foot { font-size:12px; color:#6b7280; margin-top:6px; }
      `}</style>
    </div>
  );
}
